import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { FileDown } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ProcurementDashboardTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/orders/procurement-dashboard`,
        { headers: authHeaders() });
      setRows(r.data?.rows || []);
    } catch {
      toast.error('Failed to load procurement queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function downloadCSV() {
    const header = 'Product SKU,Product Name,Total Pending Qty,Open Order Lines,Franchises Waiting,Earliest Order At\n';
    const body = rows.map(r => [
      r.product_sku || '', r.product_name || '',
      r.total_pending_qty, r.open_order_lines,
      `"${(r.franchises_waiting || []).join('; ')}"`,
      r.earliest_order_at || '',
    ].join(',')).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `procurement_queue_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-3" data-testid="procurement-dashboard-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Procurement Queue</h3>
          <p className="text-sm text-slate-600">
            SKUs with pending demand from franchise orders — raise POs to fulfil these.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="proc-refresh-btn">Refresh</Button>
          <Button size="sm" variant="outline" onClick={downloadCSV} disabled={rows.length === 0} data-testid="proc-export-btn">
            <FileDown className="w-4 h-4 mr-1" />CSV
          </Button>
        </div>
      </div>
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {!loading && rows.length === 0 && (
        <p className="text-sm text-slate-500 py-8 text-center border rounded-lg" data-testid="proc-empty">
          🎉 No pending procurement. All open orders are fully allocated.
        </p>
      )}
      {rows.length > 0 && (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-700 text-white">
              <tr>
                <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">SKU</th>
                <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Product</th>
                <th className="px-3 py-2.5 text-right text-xs uppercase tracking-wide">Pending Qty</th>
                <th className="px-3 py-2.5 text-center text-xs uppercase tracking-wide">Order Lines</th>
                <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Franchises Waiting</th>
                <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Oldest Order</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.product_id} className="border-t hover:bg-slate-50" data-testid={`proc-row-${r.product_sku}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.product_sku || '-'}</td>
                  <td className="px-3 py-2">{r.product_name}</td>
                  <td className="px-3 py-2 text-right font-semibold text-red-700">{r.total_pending_qty}</td>
                  <td className="px-3 py-2 text-center">{r.open_order_lines}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">
                    {(r.franchises_waiting || []).join(', ')}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {r.earliest_order_at ? new Date(r.earliest_order_at).toLocaleDateString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
